interface LogLevel {
  DEBUG: 'debug';
  INFO: 'info';
  WARN: 'warn';
  ERROR: 'error';
}

const LOG_LEVELS: LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
};

interface LogContext {
  requestId?: string;
  userId?: string;
  timestamp?: string;
  component?: string;
  action?: string;
  [key: string]: any;
}

class Logger {
  private isDevelopment: boolean;
  private logLevel: keyof LogLevel;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.logLevel = (import.meta.env.VITE_LOG_LEVEL as keyof LogLevel) || 'INFO';
  }

  private shouldLog(level: keyof LogLevel): boolean {
    const levels: Record<keyof LogLevel, number> = {
      DEBUG: 0,
      INFO: 1,
      WARN: 2,
      ERROR: 3,
    };

    return levels[level] >= levels[this.logLevel];
  }

  private formatMessage(level: keyof LogLevel, message: string, context?: LogContext): object {
    const logEntry = {
      '@timestamp': new Date().toISOString(),
      level: LOG_LEVELS[level],
      message,
      service: 'hinata-pal-frontend',
      environment: import.meta.env.MODE,
      version: import.meta.env.VITE_VERSION || '1.0.0',
      ...context,
    };

    return logEntry;
  }

  private logToConsole(level: keyof LogLevel, message: string, context?: LogContext): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message, context);

    switch (level) {
      case 'DEBUG':
        console.debug(formattedMessage);
        break;
      case 'INFO':
        console.info(formattedMessage);
        break;
      case 'WARN':
        console.warn(formattedMessage);
        break;
      case 'ERROR':
        console.error(formattedMessage);
        break;
    }
  }

  private logToExternal(_level: keyof LogLevel, _message: string, _context?: LogContext): void {
    // In production, you might want to send logs to an external service
    // like Elasticsearch, LogRocket, Sentry, etc.
    if (this.isDevelopment) {
      return;
    }

    try {
      // Example: Send to external logging service
      // const logEntry = this.formatMessage(_level, _message, _context);
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(logEntry),
      // });
    } catch (error) {
      console.error('Failed to send log to external service:', error);
    }
  }

  debug(message: string, context?: LogContext): void {
    this.logToConsole('DEBUG', message, context);
    this.logToExternal('DEBUG', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.logToConsole('INFO', message, context);
    this.logToExternal('INFO', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.logToConsole('WARN', message, context);
    this.logToExternal('WARN', message, context);
  }

  error(message: string, context?: LogContext): void {
    this.logToConsole('ERROR', message, context);
    this.logToExternal('ERROR', message, context);
  }

  // Convenience methods for common scenarios
  apiCall(method: string, url: string, duration?: number, status?: number): void {
    this.info('API call completed', {
      component: 'api-client',
      action: 'http-request',
      method,
      url,
      duration_ms: duration,
      status_code: status,
    });
  }

  authEvent(event: string, details?: object): void {
    this.info('Authentication event', {
      component: 'auth',
      action: event,
      ...details,
    });
  }

  navigationEvent(from: string, to: string): void {
    this.debug('Navigation event', {
      component: 'router',
      action: 'navigate',
      from,
      to,
    });
  }

  userAction(action: string, details?: object): void {
    this.info('User action', {
      component: 'ui',
      action,
      ...details,
    });
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;
export type { LogContext };